/**
 * Cart drawer upsell support.
 *
 * Two jobs, both keyed off markup the cart-drawer section re-renders on every cart
 * change, so everything here is delegated or re-bound rather than bound once:
 *
 *  1. The upsell strip's prev/next arrows, and keeping them disabled at the ends.
 *  2. Auto-applying the multi-rug discount code the moment the cart earns it. Without
 *     this the shopper has to retype the code at checkout, which is exactly where the
 *     tier we just sold them falls apart.
 */
(function () {
  'use strict';

  var CARD_STEP_FALLBACK = 168;

  /* ---------------------------------------------------------------- carousel */

  function syncArrows(root) {
    var track = root.querySelector('[data-upsell-track]');
    var prev = root.querySelector('[data-upsell-prev]');
    var next = root.querySelector('[data-upsell-next]');
    if (!track || !prev || !next) return;

    // 2px of slack absorbs sub-pixel scroll positions, which otherwise leave the
    // end arrow enabled on a track that cannot scroll any further.
    var max = track.scrollWidth - track.clientWidth;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= max - 2;
    root.classList.toggle('cart-upsell--static', max <= 2);
  }

  function step(track) {
    var card = track.querySelector('.cart-upsell__card');
    if (!card) return CARD_STEP_FALLBACK;
    var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function initCarousels() {
    document.querySelectorAll('[data-cart-upsell]').forEach(function (root) {
      if (root.dataset.upsellReady === 'true') return;
      root.dataset.upsellReady = 'true';

      var track = root.querySelector('[data-upsell-track]');
      if (!track) return;

      root.querySelector('[data-upsell-prev]')?.addEventListener('click', function () {
        track.scrollBy({ left: -step(track), behavior: 'smooth' });
      });
      root.querySelector('[data-upsell-next]')?.addEventListener('click', function () {
        track.scrollBy({ left: step(track), behavior: 'smooth' });
      });
      track.addEventListener('scroll', function () { syncArrows(root); }, { passive: true });

      syncArrows(root);
    });
  }

  /* ---------------------------------------------------------------- discount */

  // One attempt per (code, cart size) per page load. A code that is expired or
  // no longer applies returns a 200 with nothing applied, so without this guard
  // the cart:refresh it triggers would loop straight back into another attempt.
  var attempted = {};

  function applyDiscount() {
    var el = document.querySelector('[data-cart-reward]');
    if (!el) return;

    var code = el.dataset.rewardCode;
    if (!code) return;
    if (el.dataset.rewardEligible !== 'true') return;
    if (el.dataset.rewardApplied === 'true') return;

    var key = code + ':' + (el.dataset.rewardCount || '');
    if (attempted[key]) return;
    attempted[key] = true;

    fetch('/cart/update.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: code })
    })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
      })
      .catch(function (error) {
        console.error('[cart-upsell] could not apply ' + code, error);
      });
  }

  /* ---------------------------------------------------------------- tracking */

  // Delegated once, on submit rather than click: the button is the form's submit
  // control, so this fires for a keyboard activation too, and it survives every cart
  // re-render without rebinding. Intent, not outcome — a failed add still reports,
  // which is what makes the drop-off visible.
  function trackAdd(event) {
    var form = event.target.closest && event.target.closest('.cart-upsell__form');
    if (!form) return;

    var button = form.querySelector('[data-upsell-shape]');
    if (!button) return;

    if (!window.theme || !theme.utils || typeof theme.utils.track !== 'function') return;

    var price = parseInt(button.dataset.upsellPrice, 10);
    var shape = button.dataset.upsellShape || '';

    // The shape is in the event name as well as the params. The name is what Clarity
    // segments on, and the theme normalises it for GA4 — "Upsell Add Runner" arrives
    // there as upsell_add_runner. Keeping the param too means GA4 can still group the
    // shapes on one dimension rather than only comparing three event names.
    theme.utils.track(('Upsell Add ' + shape).trim(), {
      shape: shape,
      size: button.dataset.upsellSize || '',
      variant_id: button.dataset.upsellVariant || '',
      // GA4 reports currency amounts as units, and the theme carries cents.
      price: isNaN(price) ? '' : price / 100,
      // Which surface the strip was on. The two convert differently, and without this
      // they are one undifferentiated number.
      location: form.closest('cart-drawer') ? 'cart_drawer' : 'cart_page'
    });
  }

  /* ------------------------------------------------------------------- wire */

  function refresh() {
    initCarousels();
    applyDiscount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  // The drawer swaps its whole subtree in on every add/remove, which drops the
  // listeners above and brings in fresh reward data. Watching the drawer itself
  // (rather than the document) keeps the observer off every unrelated DOM write;
  // the debounce collapses the burst of mutations one re-render produces.
  var pending = null;
  function scheduleRefresh() {
    clearTimeout(pending);
    pending = setTimeout(refresh, 50);
  }

  document.addEventListener('submit', trackAdd, true);
  document.addEventListener('cart:refresh', scheduleRefresh);
  document.addEventListener('ajaxProduct:added', scheduleRefresh);

  var drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver(scheduleRefresh).observe(drawer, { childList: true, subtree: true });
  }
})();
